import React, {
  useEffect,
  useState,
} from 'react';
import {
  Link,
  useNavigate,
} from 'react-router-dom';
import {
  CheckCircle2,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  User,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { useAuth } from '@/contexts/AuthContext';

const MIN_PASSWORD_LENGTH = 6;

const Signup: React.FC = () => {
  const navigate = useNavigate();

  const {
    signUp,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] =
    useState('');
  const [password, setPassword] =
    useState('');
  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState('');
  const [submitting, setSubmitting] =
    useState(false);
  const [error, setError] =
    useState<string | null>(null);
  const [success, setSuccess] =
    useState<string | null>(null);

  useEffect(() => {
    document.title =
      'Create Account | KinsCircle';
  }, []);

  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated) {
      navigate('/app', {
        replace: true,
      });
    }
  }, [
    authLoading,
    isAuthenticated,
    navigate,
  ]);

  const handleSubmit = async (
    event: React.FormEvent,
  ) => {
    event.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    setError(null);
    setSuccess(null);

    if (!trimmedName) {
      setError(
        'Please enter your full name.',
      );
      return;
    }

    if (!trimmedEmail) {
      setError(
        'Please enter your email address.',
      );
      return;
    }

    if (
      password.length <
      MIN_PASSWORD_LENGTH
    ) {
      setError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
      );
      return;
    }

    if (password !== confirmPassword) {
      setError(
        "Passwords don't match.",
      );
      return;
    }

    setSubmitting(true);

    try {
      const result = await signUp(
        trimmedEmail,
        password,
        {
          name: trimmedName,
        },
      );

      if (!result.ok) {
        const message =
          result.error ||
          'Account creation failed.';

        if (
          message
            .toLowerCase()
            .includes('already')
        ) {
          setError(
            'An account with this email already exists. Sign in instead.',
          );
        } else {
          setError(message);
        }

        return;
      }

      setSuccess(
        'Your account was created. Check your email if confirmation is required, then sign in.',
      );

      toast.success(
        'KinsCircle account created',
      );

      setPassword('');
      setConfirmPassword('');
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Account creation failed.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-pink-50 to-amber-50 flex flex-col">
      <header className="px-4 sm:px-8 py-5 max-w-6xl mx-auto w-full">
        <Link
          to="/"
          className="inline-flex items-center gap-2"
        >
          <img
            src="/brand/kinscircle-tree-icon.png"
            alt="KinsCircle"
            className="w-10 h-10 rounded-xl object-cover shadow"
          />

          <span className="font-serif text-xl font-bold text-slate-900">
            Kins
            <span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">
              Circle
            </span>
          </span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-12">
        <section className="w-full max-w-md rounded-3xl bg-white shadow-xl border border-fuchsia-100 overflow-hidden">
          <div className="bg-gradient-to-br from-fuchsia-700 via-fuchsia-600 to-pink-600 text-white p-6">
            <div className="flex items-center gap-3">
              <img
                src="/brand/kinscircle-tree-icon.png"
                alt=""
                className="w-12 h-12 rounded-full object-cover ring-2 ring-white/30"
              />

              <div>
                <h1 className="font-serif text-2xl font-bold">
                  Create your account
                </h1>

                <p className="text-white/80 text-sm">
                  Start your private family network.
                </p>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-4"
            noValidate
          >
            <div>
              <Label htmlFor="signup-name">
                Full Name
              </Label>

              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  id="signup-name"
                  value={name}
                  onChange={(event) =>
                    setName(
                      event.target.value,
                    )
                  }
                  placeholder="Jane Walker"
                  autoComplete="name"
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="signup-email">
                Email Address
              </Label>

              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(event) =>
                    setEmail(
                      event.target.value,
                    )
                  }
                  placeholder="you@family.com"
                  autoComplete="email"
                  inputMode="email"
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="signup-password">
                Password
              </Label>

              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(event) =>
                    setPassword(
                      event.target.value,
                    )
                  }
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  minLength={
                    MIN_PASSWORD_LENGTH
                  }
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="signup-confirm">
                Confirm Password
              </Label>

              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />

                <Input
                  id="signup-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) =>
                    setConfirmPassword(
                      event.target.value,
                    )
                  }
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  minLength={
                    MIN_PASSWORD_LENGTH
                  }
                  className="pl-9"
                  required
                />
              </div>

              {confirmPassword &&
                password ===
                  confirmPassword && (
                  <p className="mt-1 text-xs text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Passwords match.
                  </p>
                )}
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                {success}
              </div>
            )}

            <Button
              type="submit"
              disabled={
                submitting ||
                authLoading
              }
              className="w-full h-11 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 text-white"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account…
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <p className="text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-fuchsia-700 hover:underline"
              >
                Sign in
              </Link>
            </p>

            <p className="text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Private. Secure. Family-only.
            </p>
          </form>
        </section>
      </main>
    </div>
  );
};

export default Signup;
